import { editorUrl } from '@/lib/editorBasePath';
import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, CheckCircle2 } from 'lucide-react';

interface AddPagesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  totalInteriorPages: number;
  defaultAfterPage?: number;
  yearbookQuantity: number;
}

const PAGE_OPTIONS = [4, 8, 12, 16, 20, 24, 28, 32];
const PRICE_PER_4_PAGES = 2; // €2 per 4 pages

const AddPagesDialog: React.FC<AddPagesDialogProps> = ({
  open,
  onOpenChange,
  totalInteriorPages,
  defaultAfterPage,
  yearbookQuantity,
}) => {
  const [pageCount, setPageCount] = useState<number>(4);
  const [afterPage, setAfterPage] = useState<number>(defaultAfterPage ?? 1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const pricePerYearbook = useMemo(() => (pageCount / 4) * PRICE_PER_4_PAGES, [pageCount]);
  const totalPrice = useMemo(() => pricePerYearbook * yearbookQuantity, [pricePerYearbook, yearbookQuantity]);

  const afterPageOptions = useMemo(() => {
    const options: number[] = [];
    for (let i = 1; i <= totalInteriorPages; i++) {
      // Only odd pages + the last page if it's even
      if (i % 2 === 1 || i === totalInteriorPages) {
        options.push(i);
      }
    }
    return options;
  }, [totalInteriorPages]);

  // Reset afterPage when defaultAfterPage changes
  React.useEffect(() => {
    if (defaultAfterPage != null) {
      setAfterPage(defaultAfterPage);
    }
  }, [defaultAfterPage]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await fetch(editorUrl('/add_page_supp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          page_count: pageCount,
          after_page: afterPage,
        }),
      });
      setShowThankYou(true);
    } catch (err) {
      console.error('Failed to add pages:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (showThankYou) {
      window.location.reload();
      return;
    }
    setPageCount(4);
    setShowThankYou(false);
    onOpenChange(false);
  };

  if (showThankYou) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="sr-only">Merci</DialogTitle>
            <DialogDescription className="sr-only">Confirmation de commande</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-6 text-center">
            <CheckCircle2 className="text-green-500" size={48} />
            <h3 className="text-lg font-semibold text-foreground">
              Merci pour votre commande.
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-sm">
              Les pages seront bientôt disponibles, Raphaëlle vous envoie très prochainement votre facture mise à jour.
            </p>
            <Button onClick={handleClose} className="mt-2">
              Fermer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else onOpenChange(v); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold text-center whitespace-nowrap">
            Ajouter des pages pour aérer mon Yearbook !
          </DialogTitle>
          <DialogDescription className="sr-only">
            Sélectionnez le nombre de pages à ajouter
          </DialogDescription>
        </DialogHeader>

        {/* Gradient divider */}
        <div className="h-0.5 w-full rounded-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500" />

        <div className="space-y-2 py-2">
          <p className="text-sm text-muted-foreground text-center leading-relaxed">
            Les pages supplémentaires s'ajoutent par 4.
            <br />
            (4 pages supplémentaires = +{PRICE_PER_4_PAGES}€ par Yearbook)
          </p>

          <div className="flex flex-col items-center gap-3 pt-4">
            {/* Page count selector */}
            <div className="flex items-center gap-3 rounded-full border-2 border-purple-300 px-5 py-2.5 w-72 justify-between">
              <span className="text-sm font-medium text-foreground">Ajouter des pages</span>
              <Select value={String(pageCount)} onValueChange={(v) => setPageCount(Number(v))}>
                <SelectTrigger className="w-20 h-8 border-0 bg-transparent shadow-none focus:ring-0 text-sm font-medium">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_OPTIONS.map((n) => (
                    <SelectItem key={n} value={String(n)}>+{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* After page selector */}
            <div className="flex items-center gap-3 rounded-full border-2 border-orange-300 px-5 py-2.5 w-72 justify-between">
              <span className="text-sm font-medium text-foreground">Après la page</span>
              <Select value={String(afterPage)} onValueChange={(v) => setAfterPage(Number(v))}>
                <SelectTrigger className="w-20 h-8 border-0 bg-transparent shadow-none focus:ring-0 text-sm font-medium">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {afterPageOptions.map((n) => (
                    <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Price display */}
            <div className="flex items-center gap-3 rounded-full border-2 border-orange-300 px-5 py-2.5 w-72 justify-between">
              <span className="text-sm font-medium text-foreground">Prix par Yearbook</span>
              <span className="text-sm font-semibold text-foreground">{pricePerYearbook}€</span>
            </div>
          </div>

          <p className="text-sm text-muted-foreground text-center pt-3">
            Soit un total de <strong>{totalPrice}€</strong> pour <strong>{yearbookQuantity} Yearbook{yearbookQuantity > 1 ? 's' : ''}</strong>
          </p>
        </div>

        <div className="flex justify-center pt-2">
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="rounded-full px-8 bg-gradient-to-r from-orange-400 via-pink-500 to-purple-500 text-white hover:opacity-90 border-0"
          >
            {isSubmitting ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
            Valider la commande
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddPagesDialog;
